import React from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { StatusType } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import { DEFAULT_NEW_PROFILE } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/utils';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import {
  apiGetProfiles,
  apiGetSingleProfile,
  apiCreateProfile,
  apiUpdateProfile
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/api/profileApis';
import { fetchUserMetaData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/ViewAdjustmentModal/utils';
import {
  ExistingProfile,
  FinalFormProfile
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/types/profileTypes';
import { mapToEditFormData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/dtoMappers';
import { useSnackbar } from 'src/utils/Hooks';
import { trackProfileCreated, trackProfileUpdated } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/tracking';
const PROFILES_KEY = 'Profiles';
const SINGLE_PROFILE_KEY = 'SingleProfile';

export const useGetProfiles = (status?: string) => {
  const queryClient = useQueryClient();

  function refetchProfiles() {
    queryClient.invalidateQueries({
      queryKey: [PROFILES_KEY, status]
    });
  }

  const { isLoading, isFetching, isRefetching, data, error } = useQuery({
    queryKey: [PROFILES_KEY, status],
    queryFn: async () => {
      const res = await apiGetProfiles(status ? { status: status as StatusType } : undefined);
      const profiles = res.data || [];
      const userIds = Array.from(new Set((profiles || []).map((p: any) => p.createdBy).filter(Boolean)));
      if (userIds.length === 0) {
        return res;
      }
      const userMetaRes = await fetchUserMetaData({ userIds });
      const userMeta = (userMetaRes.data || {}) as Record<string, { firstName: string; lastName: string }>;
      const enriched = profiles.map((p: any) => {
        const meta = p.createdBy ? userMeta[p.createdBy] : undefined;
        const createdByName = meta ? `${meta.firstName} ${meta.lastName}`.trim() : undefined;
        return { ...p, createdByName };
      });
      return { ...res, data: enriched } as typeof res;
    }
  });

  return {
    isLoadingProfiles: isLoading || isFetching || isRefetching,
    refetchProfiles,
    data,
    error
  };
};

const getExistingProfile = async ({ queryKey }) => {
  const profileId = queryKey[1];
  const response = await apiGetSingleProfile({ profileId });
  return response.data.data;
};

export const useGetSingleProfile = (profileId?: string) => {
  const queryClient = useQueryClient();

  function refetchSingleProfile() {
    if (profileId) {
      queryClient.invalidateQueries({
        queryKey: [SINGLE_PROFILE_KEY, profileId]
      });
    }
  }

  const { isLoading, isFetching, isRefetching, data, error } = useQuery({
    queryKey: [SINGLE_PROFILE_KEY, profileId],
    queryFn: getExistingProfile,
    enabled: !!profileId
  });

  return {
    isLoadingSingleProfile: isLoading || isFetching || isRefetching,
    refetchSingleProfile,
    profile: profileId ? data : DEFAULT_NEW_PROFILE,
    error
  };
};

export const useCreateProfile = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { beaconNavigator } = useBeaconNavigator();

  const { mutate, isPending, mutateAsync, error, isSuccess } = useMutation({
    mutationFn: async (profile: FinalFormProfile) => {
      return await apiCreateProfile(profile);
    },
    onSuccess: (response, variables) => {
      trackProfileCreated({
        profileId: response?.data?.data?.profileId,
        profileName: variables.profileName
      });
      queryClient.invalidateQueries({
        queryKey: [PROFILES_KEY]
      });
      showSnackbar({
        message: "You've successfully created a new content profile.",
        type: 'success'
      });
    },
    onError: () => {
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    }
  });

  return {
    createProfile: mutate,
    createProfileAsync: mutateAsync,
    isPending,
    error,
    isSuccess
  };
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const { mutate, isPending, mutateAsync, error, isSuccess } = useMutation({
    mutationFn: async (profile: FinalFormProfile) => {
      const editProfileBody = mapToEditFormData(profile);
      await apiUpdateProfile(editProfileBody);
    },
    onSuccess: (_, variables) => {
      const existingProfile = variables as ExistingProfile;
      trackProfileUpdated({
        profileId: existingProfile.profileId,
        profileName: existingProfile.profileName
      });
      queryClient.invalidateQueries({
        queryKey: [PROFILES_KEY]
      });
      queryClient.invalidateQueries({
        queryKey: [SINGLE_PROFILE_KEY, existingProfile.profileId]
      });
      showSnackbar({
        message: 'Profile updated successfully',
        type: 'success'
      });
    },
    onError: () => {
      showSnackbar({
        message: <GenericErrorMessage />,
        type: 'error'
      });
    }
  });

  return {
    updateProfile: mutate,
    updateProfileAsync: mutateAsync,
    isPending,
    error,
    isSuccess
  };
};
